import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Select as SelectAntd } from 'antd';
import { subject } from '@casl/ability';
import { Select, Popup, notification, NOTIFICATION_TYPES, Typography, RichArea } from '_atoms';
import * as actions from 'actions';
import { ACTIONS, Can, ROLES, UI, useAbility } from 'permission';
import { JOB_STATUSES } from 'constants/job';
import { getCompanies, getHrManagers, getServices } from 'store/selectors/commonSelectors';
import { getUser } from 'store/selectors/authorizeSelectors';
import { getCandidateJobs } from 'store/selectors/candidateSelectors';
import { dateFormatter } from 'helpers/dateFormatter';
import { useAppDispatch } from 'store';
import type { CandidateJobAssignment } from 'store/reducers/candidateReducer';
import type { Opening, JobWorkflowStep, JobListItem, JobListLiteItem } from 'store/reducers/jobReducer';
import { STEPS_LABELS } from 'constants/constants';
import { SELECT_TYPES } from '_atoms/Select';
import type { SelectOption } from '_atoms/Select';

interface OptionLike extends SelectOption {
  value: string;
}

type OpeningWithTeamLead = Opening & {
  team_lead: Opening['team_lead'] & { roles?: Array<{ name: string }> };
};

type SameJobCandidate = CandidateJobAssignment & {
  opening?: { candidate?: { full_name_latin?: string } };
  responsible_recruiter?: string;
};

interface ReassignCandidateInfo {
  uuid?: string;
  candidate_id?: string;
  full_name_latin?: string;
  first_name_latin?: string;
  last_name_latin?: string;
  first_name_local?: string;
  last_name_local?: string;
  speciality?: string;
}

interface PopupReassignCandidateProps {
  open: boolean;
  onClose: () => void;
  candidate: ReassignCandidateInfo;
  onAssign: () => void;
  isSSC?: boolean;
}

export const PopupReassignCandidate: React.FC<PopupReassignCandidateProps> = ({
  open,
  onClose,
  candidate,
  onAssign,
  isSSC,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const user = useSelector(getUser);
  const candidateJobs = useSelector(getCandidateJobs);
  const ability = useAbility();
  const canAssignRecruiter = ability.can(ACTIONS.READ, subject(UI.ASSIGN_RECRUITER, { status: JOB_STATUSES.OPEN }));
  const defaultRr: OptionLike | null = ability.can(
    ACTIONS.READ,
    subject(UI.ASSIGN_RECRUITER, { status: JOB_STATUSES.OPEN })
  )
    ? null
    : { value: user.full_name, label: user.full_name };

  const companies = useSelector(getCompanies);
  const services = useSelector(getServices);
  const recruiters = useSelector(getHrManagers);

  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [service, setService] = useState<OptionLike | null>(null);
  const [job, setJob] = useState<OptionLike | null>(null);
  const [opening, setOpening] = useState<OptionLike | null>(null);
  const [step, setStep] = useState<OptionLike | null>(null);
  const [comment, setComment] = useState('');
  const [jobs, setJobs] = useState<OptionLike[]>([]);
  const [openings, setOpenings] = useState<OpeningWithTeamLead[]>([]);
  const [steps, setSteps] = useState<OptionLike[]>([]);
  const [rR, setRR] = useState<OptionLike | null>(defaultRr);
  const [cvResponsible, setCvResponsible] = useState<string[]>([]);
  const [cvResponsibleOptions, setCvResponsibleOptions] = useState<Array<{ label: string; options: OptionLike[] }>>([]);
  const [candidateSameJob, setCandidateSameJob] = useState<SameJobCandidate | null>(null);
  const [jobError, setJobError] = useState('');
  const [loading, setLoading] = useState(false);

  const showCvsResponsables =
    step?.label === STEPS_LABELS.CONTACT_WITH_CANDIDATE || step?.label === STEPS_LABELS.CV_SCREENING;

  const optionsServices = useMemo(
    () =>
      services.map(item => {
        const serviceItem = item as typeof item & { company?: { uuid: string; name: string } };
        const company = companies.length
          ? ` (${companies.find(c => c.uuid === serviceItem.company?.uuid)?.name})`
          : null;
        return { label: item.name + company, value: item.uuid };
      }),
    [services, companies]
  );

  const onChangeService = (item: OptionLike) => {
    setService(item);
    setJob(null);
    setOpening(null);
    setStep(null);
    setSteps([]);
    setRR(defaultRr);
    setCvResponsible([]);
    actions.getListJobs({ service: item.value, status: 'Open' }, 'v2').then(resp => {
      const jobsList: Array<JobListItem | JobListLiteItem> = Array.isArray(resp) ? resp : resp?.results ?? [];
      setJobs(jobsList.map(i => ({ label: i.name, value: i.uuid })));
    });
    setOpenings([]);
    setJobError('');
  };

  const onAssignCandidate = () => {
    setCandidateSameJob(null);
    setLoading(true);
    actions
      .setStepToCandidate(job?.value || '', {
        candidate: isSSC ? candidate.candidate_id : candidate.uuid,
        step: step?.value,
        responsible_recruiter: canAssignRecruiter ? rR?.value : user.uuid,
        potential_opening: opening?.value,
        cv_screening_responsibles: showCvsResponsables ? cvResponsible : [],
        comment,
      })
      .then(() => {
        notification({
          message: t('translation:popup_reassign_candidate.text_candidate_assigned'),
          type: NOTIFICATION_TYPES.SUCCESS,
        });
        onAssign();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onJobChange = (item: OptionLike) => {
    setJobError('');
    setJob(item);
    setOpening(null);
    setStep(null);
    setRR(defaultRr);
    actions.getOpeningsList(item.value, { status: 'Open' }).then(opsResp => {
      const ops = (opsResp || []) as OpeningWithTeamLead[];
      setOpenings(ops);
      actions
        .getListUsersByRole({
          role: [ROLES.TEAM_LEAD, ROLES.SERVICE_HEAD, ROLES.INTERVIEWER],
          service: service.value,
          ordering: 'full_name',
        })
        .then(usersResp => {
          const users = usersResp || [];
          const teamLeadsWithOpening = ops
            .filter(o =>
              o.team_lead.roles?.find(r => ([ROLES.TEAM_LEAD, ROLES.SERVICE_HEAD] as string[]).includes(r.name))
            )
            .map(o => o.team_lead.uuid);
          const newCvResponsible = Array.from(new Set(teamLeadsWithOpening));
          setCvResponsible(newCvResponsible);
          const defaultResponsibles = users
            .filter(v => newCvResponsible.includes(v.uuid))
            .map(v => ({ value: v.uuid, label: v.full_name }));
          const availableResponsibles = users
            .filter(v => !newCvResponsible.includes(v.uuid))
            .map(v => ({ value: v.uuid, label: v.full_name }));
          const newCvResponsibleOptions: Array<{ label: string; options: OptionLike[] }> = [];
          if (defaultResponsibles.length) {
            newCvResponsibleOptions.push({
              label: t('translation:popup_reassign_candidate.text_default_responsibles'),
              options: defaultResponsibles,
            });
          }
          newCvResponsibleOptions.push({
            label: t('translation:popup_reassign_candidate.text_available_responsibles'),
            options: availableResponsibles,
          });

          setCvResponsibleOptions(newCvResponsibleOptions);
        });
    });
    actions.getStepsAssignToJob(item.value).then(resp => {
      const steps2: JobWorkflowStep[] = resp || [];
      const data = steps2
        .filter(i => i.ordering_number !== 6)
        .map(itemFiltered => ({ label: itemFiltered.name ?? '', value: itemFiltered.uuid }));
      setSteps(data);
    });
  };
  useEffect(() => {
    if (!initialDataLoaded && open) {
      setInitialDataLoaded(true);
      dispatch(actions.getAllCompanies());
      dispatch(actions.getListServices());
      dispatch(
        actions.getListHrManagers({
          role: [ROLES.RECRUITER, ROLES.RECRUITER_LEAD, ROLES.HR_LEAD],
          my_recruiters_group: user.active_role.name === ROLES.RECRUITER_LEAD,
          ordering: 'full_name',
        })
      );
      if (!isSSC) dispatch(actions.getJobs(candidate.uuid));
    }
  }, [initialDataLoaded, open]);

  useEffect(() => {
    setService(null);
    setJob(null);
    setOpening(null);
    setStep(null);
    setSteps([]);
    setJobs([]);
    setOpenings([]);
    setCandidateSameJob(null);
    setRR(null);
    setCvResponsible([]);
    setComment('');
    setJobError('');
  }, [open]);

  if (candidateSameJob) {
    return (
      <Popup
        open={!!candidateSameJob}
        title={t('translation:popup_reassign_candidate.title_reassign')}
        cancelButtonProps={{
          onClick: () => setCandidateSameJob(null),
          children: t('translation:popup_reassign_candidate.button_cancel'),
        }}
        okButtonProps={{
          onClick: onAssignCandidate,
          children: t('translation:popup_reassign_candidate.button_assign_to_job'),
        }}
      >
        {t('translation:popup_reassign_candidate.text_reassign_confirmation', {
          status: candidateSameJob.active_step?.step_status,
          job: candidateSameJob.job,
          responsible_recruiter: candidateSameJob.responsible_recruiter,
          step: step?.label,
          recruiter: rR?.label,
        } as Record<string, unknown>)}
      </Popup>
    );
  }

  return (
    <Popup
      open={open}
      title={t('translation:popup_reassign_candidate.title_assign')}
      className="popup-assign-candidate-to-job"
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:popup_reassign_candidate.button_cancel'),
      }}
      okButtonProps={{
        disabled: !service || !job || !step || (canAssignRecruiter && !rR),
        onClick: () => {
          const sameJob = candidateJobs.find(j => j.active_step && j.job_uuid === job?.value);
          if (sameJob && sameJob.candidate_status === 'Hired') {
            notification({
              message: t('translation:popup_reassign_candidate.text_candidate_already_passed', {
                name: (sameJob as SameJobCandidate).opening?.candidate?.full_name_latin,
              } as Record<string, unknown>),
            });
            return;
          }
          if (sameJob && sameJob.candidate_status !== 'Rejected') {
            const error = t('translation:popup_reassign_candidate.text_candidate_already_assigned', {
              step: sameJob.active_step?.name,
            } as Record<string, unknown>);
            setJobError(error);
            notification({
              message: error,
              type: NOTIFICATION_TYPES.ERROR,
            });
            return;
          }
          if (sameJob) setCandidateSameJob(sameJob as SameJobCandidate);
          else onAssignCandidate();
        },
        children: t('translation:popup_reassign_candidate.button_assign'),
        loading,
      }}
    >
      <div className="user-info">
        <Typography.Text className="user-info__title">
          {candidate.full_name_latin
            ? candidate.full_name_latin
            : `${candidate.first_name_latin} ${candidate.last_name_latin}`}
        </Typography.Text>
        <Typography.Caption className="user-info__subtitle">
          {candidate.first_name_local} {candidate.last_name_local}
        </Typography.Caption>
        <Typography.Caption className="user-info__subtitle">{candidate.speciality}</Typography.Caption>
      </div>
      <Select
        labelInValue
        label={t('translation:popup_reassign_candidate.label_service')}
        placeholder={t('translation:popup_reassign_candidate.placeholder_select_service')}
        value={service}
        options={optionsServices}
        onChange={onChangeService}
      />
      <Select
        labelInValue
        label={t('translation:popup_reassign_candidate.label_job')}
        placeholder={t('translation:popup_reassign_candidate.placeholder_select_job')}
        value={job}
        disabled={!jobs.length}
        options={jobs}
        itemProps={{
          validateStatus: jobError ? 'error' : '',
          help: jobError,
        }}
        onChange={onJobChange}
      />
      <Select
        labelInValue
        label={t('translation:popup_reassign_candidate.label_potential_opening')}
        placeholder={t('translation:popup_reassign_candidate.placeholder_select_potential_opening')}
        value={opening}
        onChange={setOpening}
        disabled={!job || !openings.length}
        optional
      >
        {openings.map(o => (
          <SelectAntd.Option key={o.uuid} value={o.uuid} label={`${o.level} - ${o.team_lead.full_name}`}>
            <div className="opening-select-option">
              <Typography.Text>{`${o.level} - ${o.team_lead.full_name}`}</Typography.Text>
              <Typography.Caption>
                {t('translation:popup_reassign_candidate.text_expected_date', {
                  date: dateFormatter(o.expected),
                } as Record<string, unknown>)}
              </Typography.Caption>
              <Typography.Caption>{(o.offices || []).map(office => office.name).join(', ')}</Typography.Caption>
            </div>
          </SelectAntd.Option>
        ))}
      </Select>
      <Can I={ACTIONS.READ} this={subject(UI.ASSIGN_RECRUITER, { status: JOB_STATUSES.OPEN })}>
        <Select
          labelInValue
          label={t('translation:popup_reassign_candidate.label_recruiter')}
          placeholder={t('translation:popup_reassign_candidate.placeholder_select_recruiter')}
          value={rR}
          disabled={!recruiters.length}
          options={recruiters}
          onChange={setRR}
        />
      </Can>
      <Select
        labelInValue
        label={t('translation:popup_reassign_candidate.label_step')}
        placeholder={t('translation:popup_reassign_candidate.placeholder_select_step')}
        value={step}
        disabled={!steps.length}
        options={steps}
        onChange={(item: OptionLike) => setStep(item)}
      />
      {showCvsResponsables && (
        <Select
          label={t('translation:popup_reassign_candidate.label_cv_screening_responsibles')}
          value={cvResponsible}
          placeholder={t('translation:popup_reassign_candidate.placeholder_select_cv_screening_responsibles')}
          options={cvResponsibleOptions}
          onChange={(r: OptionLike[]) => setCvResponsible(r.map(v => v.value))}
          mode={SELECT_TYPES.MULTIPLE}
          optionFilterProp="label"
          optional
        />
      )}
      <RichArea
        label={t('translation:popup_reassign_candidate.label_comment')}
        maxLength={6000}
        placeholder={t('translation:popup_reassign_candidate.placeholder_comment')}
        onChange={setComment}
        optional
      />
    </Popup>
  );
};
